import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

import {
  DEFAULT_ADAPTIVE_RADIUS,
  DEFAULT_ELEMENT_STROKE_COLOR_PALETTE,
  DEFAULT_ELEMENT_STROKE_PICKS,
  DEFAULT_FONT_SIZE,
  MIN_FONT_SIZE,
  ROUNDNESS,
  SCREEN_SIZE_PRESETS,
  colorToHSVA,
  findPresetIndex,
  getScreenDurationMs,
  hsvaToColor,
  isTransparent,
} from "@excalidraw/common";
import {
  CaptureUpdateAction,
  shouldAllowVerticalAlign,
  suppportsHorizontalAlign,
  getBoundTextElement,
  hasBoundTextElement,
  hasStrokeColor,
  isArrowElement,
  isElbowArrow,
  isFrameLikeElement,
  isImageElement,
  isLinearElement,
  isTextElement,
  redrawTextBoundingBox,
  showSelectedShapeActions,
  toolIsArrow,
  updateBindings,
} from "@excalidraw/element";

import { degreesToRadians, radiansToDegrees } from "@excalidraw/math";

import type { Degrees } from "@excalidraw/math";

import type { CornerRadii } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawTextElement,
} from "@excalidraw/element/types";

import {
  alignActionsPredicate,
  actionAlignLeft,
  actionAlignRight,
  actionAlignTop,
  actionAlignBottom,
  actionAlignHorizontallyCentered,
  actionAlignVerticallyCentered,
} from "../../actions/actionAlign";
import {
  actionFlipHorizontal,
  actionFlipVertical,
} from "../../actions/actionFlip";
import { actionChangeFontSize } from "../../actions/actionProperties";
import {
  canChangeRoundness,
  canHaveArrowheads,
  getTargetElements,
  hasBackground,
  hasFreedrawMode,
  hasStrokeStyle,
  hasStrokeWidth,
} from "../../scene";
import { t } from "../../i18n";

import {
  useApp,
  useExcalidrawActionManager,
  useExcalidrawAppState,
  useExcalidrawElements,
  useExcalidrawSetAppState,
} from "../App";
import { canChangeStrokeColor, canChangeBackgroundColor } from "../Actions";
import { ColorPicker } from "../ColorPicker/ColorPicker";
import {
  StrokeWidthBaseIcon,
  fontSizeIcon,
  EdgeRoundIcon,
  flipHorizontal,
  flipVertical,
  resetRotationIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  AlignBottomIcon,
  CenterHorizontallyIcon,
  CenterVerticallyIcon,
  LockedIcon,
  UnlockedIcon,
  eyeIcon,
  eyeClosedIcon,
  gridIcon,
  gridDotsIcon,
  slashIcon,
} from "../icons";
import Position from "../Stats/Position";
import MultiPosition from "../Stats/MultiPosition";
import Dimension from "../Stats/Dimension";
import MultiDimension from "../Stats/MultiDimension";
import Angle from "../Stats/Angle";
import MultiAngle from "../Stats/MultiAngle";
import { getAtomicUnits } from "../Stats/utils";

import "./DesignPanel.scss";

const PanelSection = ({
  title,
  heading = false,
  actions,
  children,
}: {
  /** Abschnitt 20: optional — Effects renders its own heading row inside
   * EffectsPanel itself (title + grid/plus icons all read the action's own
   * live state, which this component doesn't have direct access to), so
   * PanelSection is used there purely for the shared section divider/
   * spacing, with no title of its own to avoid a duplicate row. */
  title?: string;
  /** Abschnitt 16: larger, normal-case main heading instead of the small
   * caps section title — POSITION only for now, other sections opt in as
   * they're each restructured the same way. */
  heading?: boolean;
  /** Optional small icon buttons right-aligned in the heading row. Currently
   * unused (Fill/Stroke dropped their grid/plus icons), kept as generic
   * heading infrastructure for future sections. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="design-panel__section">
    {title && (
      <div
        className={
          heading
            ? "design-panel__section-heading"
            : "design-panel__section-title"
        }
      >
        <span>{title}</span>
        {actions && (
          <div className="design-panel__heading-actions">{actions}</div>
        )}
      </div>
    )}
    {children}
  </section>
);

// Abschnitt 16: two 3-button alignment groups (horizontal: left/center/
// right, vertical: top/middle/bottom). Rendered as own buttons via
// executeAction rather than renderAction — the actions' PanelComponents
// hide themselves (`hidden={!alignActionsPredicate(...)}`) whenever fewer
// than two elements are selected, which would make the whole Alignment
// block silently vanish for the everyday single-selection case. Here the
// buttons stay visible and are merely disabled instead.
const ALIGN_GROUPS = [
  [
    { action: actionAlignLeft, icon: AlignLeftIcon, label: "labels.alignLeft" },
    {
      action: actionAlignHorizontallyCentered,
      icon: CenterHorizontallyIcon,
      label: "labels.centerHorizontally",
    },
    {
      action: actionAlignRight,
      icon: AlignRightIcon,
      label: "labels.alignRight",
    },
  ],
  [
    { action: actionAlignTop, icon: AlignTopIcon, label: "labels.alignTop" },
    {
      action: actionAlignVerticallyCentered,
      icon: CenterVerticallyIcon,
      label: "labels.centerVertically",
    },
    {
      action: actionAlignBottom,
      icon: AlignBottomIcon,
      label: "labels.alignBottom",
    },
  ],
] as const;

/**
 * Figma-style numeric property field: icon + editable number, committed on
 * Enter/blur. `value === null` renders the mixed-selection state.
 */
const OpacityGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="7" />
    <path d="M10 3a7 7 0 0 1 0 14z" fill="currentColor" stroke="none" />
  </svg>
);

const DurationGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="10" r="7" />
    <path d="M10 6v4l3 2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Independent-corner controls. One glyph drawn once (a top-left corner) and
// rotated for the other three, in the same clockwise-from-top-left order as
// `roundness.corners`.
const CORNER_LABELS = [
  "Top left",
  "Top right",
  "Bottom right",
  "Bottom left",
] as const;

const cornerGlyph = (rotation: number) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    style={{ transform: `rotate(${rotation}deg)` }}
  >
    <path d="M5 16V9a4 4 0 0 1 4-4h7" />
  </svg>
);

const CORNER_GLYPHS = [0, 90, 180, 270].map(cornerGlyph);

// roundness.corners is clockwise [TL, TR, BR, BL] (geometry order); the 2x2
// grid reads row-major, so rendering in that order would put BR under TL and
// BL under TR — swapped left/right on the bottom row. Reorder for display only.
const CORNER_GRID_ORDER = [0, 1, 3, 2] as const;

const IndependentCornersIcon = (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <path d="M4 8V6a2 2 0 0 1 2-2h2" />
    <path d="M12 4h2a2 2 0 0 1 2 2v2" />
    <path d="M16 12v2a2 2 0 0 1-2 2h-2" />
    <path d="M8 16H6a2 2 0 0 1-2-2v-2" />
  </svg>
);

// Abschnitt 19: per-fill/per-stroke opacity is the ALPHA CHANNEL of the
// element's backgroundColor/strokeColor (#RRGGBBAA — tinycolor-backed, the
// color picker's own Alpha slider already writes this format). This is a
// separate concept from element.opacity (the Appearance field), which
// multiplies fill AND stroke together on top of these.
const withAlpha = (color: string, alpha: number) =>
  hsvaToColor({ ...colorToHSVA(color), a: alpha });

const alphaPercentOf = (color: string) =>
  Math.round(colorToHSVA(color).a * 100);

const PropertyNumberInput = ({
  icon,
  suffix,
  action,
  value,
  min,
  max,
  step = 1,
  title,
  onCommit,
}: {
  icon?: React.ReactNode;
  suffix?: string;
  /** small trailing control rendered inside the pill (must stop its own
   * pointerdown — the pill itself is a scrub target) */
  action?: React.ReactNode;
  value: number | null;
  min: number;
  max: number;
  step?: number;
  title: string;
  /** capture=false marks a transient mid-scrub update (folded into the next
   * undo entry via CaptureUpdateAction.EVENTUALLY); the drag's final commit
   * and every typed commit pass capture=true. */
  onCommit: (value: number, capture?: boolean) => void;
}) => {
  const [inputValue, setInputValue] = useState(
    value === null ? "" : String(value),
  );
  // Figma-style scrubbing: horizontal drag anywhere on the field except the
  // text input itself steps the value (1px = one step), same interaction as
  // the X/Y/W/H DragInput fields. Refs, not state — pointermove must read/
  // write within the same event burst without waiting on a re-render.
  const dragRef = useRef<{
    startX: number;
    startValue: number;
    moved: boolean;
    last: number;
  } | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    setInputValue(value === null ? "" : String(value));
  }, [value]);

  const clampToStep = (raw: number) => {
    const clamped = Math.min(max, Math.max(min, raw));
    // snap to the step grid and cut float noise (0.30000000000000004 → 0.3)
    return parseFloat((Math.round(clamped / step) * step).toFixed(2));
  };

  const commit = (raw: string) => {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      setInputValue(String(clamped));
      onCommit(clamped);
    } else {
      // revert invalid input
      setInputValue(value === null ? "" : String(value));
    }
  };

  return (
    <label
      className="design-panel__number-input"
      title={title}
      onPointerDown={(event) => {
        if (event.target instanceof HTMLInputElement) {
          return;
        }
        const parsed = parseFloat(inputValue);
        const startValue = !isNaN(parsed) ? parsed : value ?? min;
        dragRef.current = {
          startX: event.clientX,
          startValue,
          moved: false,
          last: startValue,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag) {
          return;
        }
        const dx = event.clientX - drag.startX;
        // 2px dead zone so a plain click never counts as a scrub
        if (!drag.moved && Math.abs(dx) < 2) {
          return;
        }
        drag.moved = true;
        const next = clampToStep(drag.startValue + dx * step);
        if (next !== drag.last) {
          drag.last = next;
          setInputValue(String(next));
          onCommit(next, false);
        }
      }}
      onPointerUp={() => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (drag?.moved) {
          suppressClickRef.current = true;
          onCommit(drag.last, true);
        }
      }}
      onClickCapture={(event) => {
        // a scrub that moved must not also trigger the label's default
        // click behavior (focusing the input) on release
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          event.preventDefault();
        }
      }}
    >
      {icon}
      <input
        type="number"
        value={inputValue}
        min={min}
        max={max}
        step={step}
        placeholder={value === null ? "Mixed" : undefined}
        onChange={(event) => setInputValue(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commit(event.currentTarget.value);
            event.currentTarget.blur();
          }
        }}
      />
      {suffix && <span className="design-panel__input-suffix">{suffix}</span>}
      {action}
    </label>
  );
};

export const DesignPanel: React.FC = () => {
  const app = useApp();
  const appState = useExcalidrawAppState();
  const setAppState = useExcalidrawSetAppState();
  const actionManager = useExcalidrawActionManager();
  // subscribe to element changes so inputs stay in sync with the canvas
  useExcalidrawElements();
  // own popup toggle so it doesn't fight the shape stroke picker for the
  // shared appState.openPopup === "elementStroke" slot when both are shown
  const [textColorPopupOpen, setTextColorPopupOpen] = useState(false);
  // Abschnitt 17: aspect-ratio lock for the W/H fields. UI-only state (like
  // the popup toggle above) — the proportional resize itself is the
  // existing engine behind Dimension's shouldKeepAspectRatio flag.
  const [aspectRatioLocked, setAspectRatioLocked] = useState(false);
  // independent corner radii: UI-only "expanded" flag. The elements
  // themselves are the source of truth (roundness.corners) — this only keeps
  // the four fields open while they're all still equal to the uniform value.
  const [independentCorners, setIndependentCorners] = useState(false);
  // Abschnitt 19: eye-icon visibility toggles. "Hidden" is alpha 0 on the
  // fill/stroke color; these remember the pre-hide color per element id so
  // showing again restores the exact previous alpha, not just 100%.
  const hiddenFillColors = useRef(new Map<string, string>());
  const hiddenStrokeColors = useRef(new Map<string, string>());

  if (!app?.scene || !actionManager) {
    return null;
  }

  const { scene } = app;
  const elementsMap = scene.getNonDeletedElementsMap();
  const targetElements = getTargetElements(elementsMap, appState);
  const renderAction = actionManager.renderAction;

  const singleElement = targetElements.length === 1 ? targetElements[0] : null;
  const multipleElements = targetElements.length > 1 ? targetElements : null;

  // a "screen" is a frame element — expose screen size presets + playback
  // duration when a single frame is selected (W/H live in Layout, background in
  // Fill above)
  const singleFrame =
    singleElement && isFrameLikeElement(singleElement) ? singleElement : null;

  const applyScreenPreset = (width: number, height: number) => {
    if (!singleFrame) {
      return;
    }
    scene.mutateElement(singleFrame, { width, height });
    app.syncActionResult({ captureUpdate: CaptureUpdateAction.IMMEDIATELY });
  };

  const setScreenDuration = (seconds: number, capture = true) => {
    if (!singleFrame) {
      return;
    }
    scene.mutateElement(singleFrame, {
      customData: {
        ...singleFrame.customData,
        screenDurationMs: Math.max(100, Math.round(seconds * 1000)),
      },
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  const isSingleElementBoundContainer =
    targetElements.length === 2 &&
    (hasBoundTextElement(targetElements[0]) ||
      hasBoundTextElement(targetElements[1]));

  const isEditingTextOrNewElement = Boolean(
    appState.editingTextElement || appState.newElement,
  );

  const showFillIcons =
    (hasBackground(appState.activeTool.type) &&
      !isTransparent(appState.currentItemBackgroundColor)) ||
    targetElements.some(
      (element) =>
        hasBackground(element.type) && !isTransparent(element.backgroundColor),
    );

  const showLinkIcon =
    targetElements.length === 1 || isSingleElementBoundContainer;

  const showLineEditorAction =
    !appState.selectedLinearElement?.isEditing &&
    targetElements.length === 1 &&
    isLinearElement(targetElements[0]) &&
    !isElbowArrow(targetElements[0]);

  const showCropEditorAction =
    !appState.croppingElementId &&
    targetElements.length === 1 &&
    isImageElement(targetElements[0]);

  const showAlignActions =
    !isSingleElementBoundContainer && alignActionsPredicate(appState, app);

  const showStrokeColor = canChangeStrokeColor(appState, targetElements);
  const showBackground = canChangeBackgroundColor(appState, targetElements);
  const showStrokeWidth =
    hasStrokeWidth(appState.activeTool.type) ||
    targetElements.some((element) => hasStrokeWidth(element.type));
  const showStrokeStyle =
    hasStrokeStyle(appState.activeTool.type) ||
    targetElements.some((element) => hasStrokeStyle(element.type));
  const showFreedrawMode =
    hasFreedrawMode(appState.activeTool.type) ||
    targetElements.some((element) => hasFreedrawMode(element.type));
  const showRoundness =
    canChangeRoundness(appState.activeTool.type) ||
    targetElements.some((element) => canChangeRoundness(element.type));
  const showArrowProps =
    toolIsArrow(appState.activeTool.type) ||
    targetElements.some((element) => toolIsArrow(element.type));
  const showArrowheads =
    canHaveArrowheads(appState.activeTool.type) ||
    targetElements.some((element) => canHaveArrowheads(element.type));
  const showTextProps =
    appState.activeTool.type === "text" || targetElements.some(isTextElement);

  const atomicUnits = multipleElements
    ? getAtomicUnits(targetElements, appState)
    : [];

  // ---- custom stroke width (Figma-style numeric override of the presets) ----
  const strokeWidthElements = targetElements.filter((element) =>
    hasStrokeWidth(element.type),
  );
  const commonStrokeWidth =
    strokeWidthElements.length &&
    strokeWidthElements.every(
      (element) => element.strokeWidth === strokeWidthElements[0].strokeWidth,
    )
      ? strokeWidthElements[0].strokeWidth
      : null;

  const setCustomStrokeWidth = (width: number, capture = true) => {
    strokeWidthElements.forEach((element) => {
      scene.mutateElement(element, { strokeWidth: width });
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  // ---- custom font size (numeric override of the S/M/L/XL presets) ----
  // mirrors the presets' target set: a selected text element, or the bound
  // text of a selected container
  const fontSizeElements = targetElements
    .map((element) =>
      isTextElement(element)
        ? element
        : getBoundTextElement(element, elementsMap),
    )
    .filter((element): element is ExcalidrawTextElement => !!element);
  const commonFontSize = fontSizeElements.length
    ? fontSizeElements.every(
        (element) => element.fontSize === fontSizeElements[0].fontSize,
      )
      ? fontSizeElements[0].fontSize
      : null
    : appState.currentItemFontSize ?? DEFAULT_FONT_SIZE;

  const roughTextOn = fontSizeElements.length
    ? fontSizeElements.some((element) => element.roughText)
    : appState.currentItemRoughText;

  const setCustomFontSize = (size: number, capture = true) => {
    if (capture) {
      // the action re-lays out the text, re-anchors it and updates bindings
      actionManager.executeAction(actionChangeFontSize, "ui", size);
      return;
    }
    // mid-scrub: same re-layout, but folded into the next undo entry
    fontSizeElements.forEach((element) => {
      scene.mutateElement(element, { fontSize: size });
      redrawTextBoundingBox(element, scene.getContainerElement(element), scene);
    });
    app.syncActionResult({ captureUpdate: CaptureUpdateAction.EVENTUALLY });
  };

  // ---- Abschnitt 19: per-fill / per-stroke opacity, visibility, remove ----
  // Same element filters the changeBackgroundColor/changeStrokeColor actions
  // use, so these controls always target exactly what the color chip edits.
  const fillElements = targetElements.filter((element) =>
    hasBackground(element.type),
  );
  const strokeColorElements = targetElements.filter((element) =>
    hasStrokeColor(element.type),
  );

  const commonAlphaPercent = (
    elements: readonly ExcalidrawElement[],
    colorOf: (element: ExcalidrawElement) => string,
  ) =>
    elements.length &&
    elements.every(
      (element) =>
        alphaPercentOf(colorOf(element)) ===
        alphaPercentOf(colorOf(elements[0])),
    )
      ? alphaPercentOf(colorOf(elements[0]))
      : null;

  const commonFillAlpha = commonAlphaPercent(
    fillElements,
    (element) => element.backgroundColor,
  );
  const commonStrokeAlpha = commonAlphaPercent(
    strokeColorElements,
    (element) => element.strokeColor,
  );

  // NOTE: when an element has a backgroundGradient, the gradient overrides
  // backgroundColor for rendering — this field then edits only the fallback
  // swatch color (per-stop alpha lives in the gradient picker instead).
  const setFillAlpha = (percent: number, capture = true) => {
    fillElements.forEach((element) => {
      // the literal "transparent" keyword has no RGB to keep — nothing
      // meaningful to apply an alpha to
      if (element.backgroundColor === "transparent") {
        return;
      }
      scene.mutateElement(element, {
        backgroundColor: withAlpha(element.backgroundColor, percent / 100),
      });
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  const setStrokeAlpha = (percent: number, capture = true) => {
    strokeColorElements.forEach((element) => {
      if (element.strokeColor === "transparent") {
        return;
      }
      scene.mutateElement(element, {
        strokeColor: withAlpha(element.strokeColor, percent / 100),
      });
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  const fillsHidden =
    fillElements.length > 0 &&
    fillElements.every((element) => isTransparent(element.backgroundColor));
  const strokesHidden =
    strokeColorElements.length > 0 &&
    strokeColorElements.every((element) => isTransparent(element.strokeColor));

  const togglePaintVisibility = (
    elements: readonly ExcalidrawElement[],
    hidden: boolean,
    colorProp: "backgroundColor" | "strokeColor",
    stored: Map<string, string>,
    fallbackColor: string,
  ) => {
    const applyColor = (element: ExcalidrawElement, color: string) => {
      scene.mutateElement(
        element,
        colorProp === "backgroundColor"
          ? { backgroundColor: color }
          : { strokeColor: color },
      );
    };
    elements.forEach((element) => {
      const current = element[colorProp];
      if (hidden) {
        const previous = stored.get(element.id);
        stored.delete(element.id);
        // restore the remembered color; for a fill/stroke that was already
        // "transparent" (removed) fall back to the current default color
        const restored =
          previous ??
          (current !== "transparent" ? withAlpha(current, 1) : fallbackColor);
        if (restored && !isTransparent(restored)) {
          applyColor(element, restored);
        }
      } else if (!isTransparent(current)) {
        stored.set(element.id, current);
        applyColor(element, withAlpha(current, 0));
      }
    });
    app.syncActionResult({ captureUpdate: CaptureUpdateAction.IMMEDIATELY });
  };

  // ---- step rotation through 45° stops (0 → 45 → 90 → … → 315 → 0) — no
  // existing action for this in the codebase (checked actionAlign.tsx/
  // actionFlip.ts and the wider actions/ directory), so implemented as a
  // local handler the same way as the other custom-value overrides above/
  // below, mirroring exactly what Stats/Angle.tsx's own drag-to-angle
  // handler does for a single element (bound-text angle sync,
  // updateBindings for arrows) but for every selected element at once.
  const stepRotation = () => {
    const elementsMap = scene.getNonDeletedElementsMap();
    targetElements.forEach((element) => {
      if (isElbowArrow(element)) {
        return;
      }
      // each element advances from its own angle to the next 45° stop —
      // an off-grid angle (e.g. 30°) snaps forward to the nearest one (45°)
      const degrees = radiansToDegrees(element.angle);
      const next = degreesToRadians(
        ((Math.floor(degrees / 45 + 1e-6) * 45 + 45) % 360) as Degrees,
      );
      scene.mutateElement(element, { angle: next });
      updateBindings(element, scene, appState);
      const boundTextElement = getBoundTextElement(element, elementsMap);
      if (boundTextElement && !isArrowElement(element)) {
        scene.mutateElement(boundTextElement, { angle: next });
      }
    });
    app.syncActionResult({
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  // ---- custom corner radius (numeric override of the sharp/round presets) ----
  const roundnessElements = targetElements.filter((element) =>
    canChangeRoundness(element.type),
  );
  const cornerRadiusOf = (element: ExcalidrawElement) =>
    element.roundness ? element.roundness.value ?? DEFAULT_ADAPTIVE_RADIUS : 0;
  const commonCornerRadius =
    roundnessElements.length &&
    roundnessElements.every(
      (element) =>
        cornerRadiusOf(element) === cornerRadiusOf(roundnessElements[0]),
    )
      ? cornerRadiusOf(roundnessElements[0])
      : null;

  // ---- independent corners (Figma's expanded corner-radius control) ----
  // Only rectanguloids have four addressable corners: diamond and line round
  // via the proportional algorithm, which has no per-corner notion at all
  // (getCornerRadii ignores `corners` for them), so they keep the single field.
  const perCornerElements = roundnessElements.filter(
    (element) => element.type !== "diamond" && element.type !== "line",
  );
  const cornerRadiiOf = (element: ExcalidrawElement): CornerRadii => {
    const radius = cornerRadiusOf(element);
    const corners = element.roundness?.corners;
    return corners
      ? [corners[0], corners[1], corners[2], corners[3]]
      : [radius, radius, radius, radius];
  };
  // expanded whenever any selected element already carries per-corner values,
  // so reselecting a shape doesn't silently collapse (and hide) them
  const showIndependentCorners =
    perCornerElements.length > 0 &&
    (independentCorners ||
      perCornerElements.some((element) => !!element.roundness?.corners));
  // one entry per corner; null = mixed across the selection ("Mixed")
  const perCornerRadii = perCornerElements.map((element) =>
    cornerRadiiOf(element),
  );
  const commonCornerRadii = CORNER_LABELS.map((_, index) =>
    perCornerRadii.length &&
    perCornerRadii.every((radii) => radii[index] === perCornerRadii[0][index])
      ? perCornerRadii[0][index]
      : null,
  );

  // ---- text color, independent of the container's own stroke color ----
  const textColorElements = targetElements.filter(isTextElement);
  const commonTextColor =
    textColorElements.length &&
    textColorElements.every(
      (element) => element.strokeColor === textColorElements[0].strokeColor,
    )
      ? textColorElements[0].strokeColor
      : null;

  const setTextColor = (color: string) => {
    textColorElements.forEach((element) => {
      scene.mutateElement(element, { strokeColor: color });
    });
    app.syncActionResult({
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  // ---- opacity as a Figma-style numeric field ----
  const commonOpacity =
    targetElements.length &&
    targetElements.every(
      (element) => element.opacity === targetElements[0].opacity,
    )
      ? targetElements[0].opacity
      : null;

  const setCustomOpacity = (opacity: number, capture = true) => {
    targetElements.forEach((element) => {
      scene.mutateElement(element, { opacity: Math.round(opacity) });
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  const setCustomCornerRadius = (radius: number, capture = true) => {
    // this field writes all four corners at once, which drops the per-corner
    // override below — pin the expanded state so the four fields don't vanish
    // out from under the cursor mid-edit; they just all read the same value now
    if (showIndependentCorners) {
      setIndependentCorners(true);
    }
    roundnessElements.forEach((element) => {
      scene.mutateElement(element, {
        roundness:
          radius > 0
            ? {
                type: isLinearElement(element)
                  ? ROUNDNESS.PROPORTIONAL_RADIUS
                  : ROUNDNESS.ADAPTIVE_RADIUS,
                value: radius,
                // the single field always writes all four corners at once
                corners: null,
              }
            : null,
      });
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  const setCornerRadiusAt = (index: number, radius: number, capture = true) => {
    perCornerElements.forEach((element) => {
      const corners = cornerRadiiOf(element);
      corners[index] = radius;
      scene.mutateElement(element, {
        roundness: {
          type: ROUNDNESS.ADAPTIVE_RADIUS,
          // `value` stays the uniform fallback every non-rectanguloid reader
          // (hit-testing, bindings, the collapsed field) still goes through —
          // keep it at the largest corner so those stay in the right ballpark.
          // ponytail: hit-test outlines remain uniform; per-corner collision
          // geometry only matters if binding to a corner feels off.
          value: Math.max(...corners),
          corners: [corners[0], corners[1], corners[2], corners[3]],
        },
      });
    });
    app.syncActionResult({
      captureUpdate: capture
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    });
  };

  const toggleIndependentCorners = () => {
    if (showIndependentCorners) {
      // collapsing resets to a single uniform radius (the largest corner, so
      // nothing visually "disappears")
      perCornerElements.forEach((element) => {
        if (element.roundness?.corners) {
          scene.mutateElement(element, {
            roundness: {
              type: element.roundness.type,
              value: Math.max(...cornerRadiiOf(element)),
              corners: null,
            },
          });
        }
      });
      app.syncActionResult({ captureUpdate: CaptureUpdateAction.IMMEDIATELY });
      setIndependentCorners(false);
      return;
    }
    setIndependentCorners(true);
  };

  // when a drawing tool is active with no selection, still show the style
  // sections so the user can set defaults before drawing (parity with the
  // classic shape-actions panel)
  const showStyleDefaults = showSelectedShapeActions(
    appState,
    scene.getNonDeletedElements(),
  );

  // brush/eraser size + opacity live here rather than in the floating
  // BrushPanel, so all numeric styling reads from one place
  const isEraserTool = appState.activeTool.type === "eraser";
  const isBrushTool = appState.activeTool.type === "brush" || isEraserTool;
  const brushSection = isBrushTool && (
    <PanelSection title={isEraserTool ? "Eraser" : "Brush"} heading>
      <div className="design-panel__inputs">
        <div className="design-panel__field">
          <h3>Size</h3>
          <PropertyNumberInput
            icon={StrokeWidthBaseIcon}
            suffix="px"
            title={isEraserTool ? "Eraser size" : "Brush size"}
            value={
              isEraserTool
                ? appState.currentEraserSize
                : appState.currentItemBrushSize
            }
            min={1}
            max={128}
            onCommit={(size) => {
              if (isEraserTool) {
                setAppState({ currentEraserSize: size });
              } else {
                setAppState({ currentItemBrushSize: size });
              }
            }}
          />
        </div>
        {!isEraserTool && (
          <div className="design-panel__field">
            <h3>Opacity</h3>
            <PropertyNumberInput
              icon={OpacityGlyph}
              suffix="%"
              title="Brush opacity"
              value={appState.currentItemOpacity}
              min={0}
              max={100}
              onCommit={(opacity) =>
                setAppState({ currentItemOpacity: opacity })
              }
            />
          </div>
        )}
      </div>
    </PanelSection>
  );

  if (targetElements.length === 0 && !showStyleDefaults) {
    return (
      <div className="design-panel selected-shape-actions">
        {brushSection}
        <PanelSection title="Canvas" heading>
          <div className="design-panel__row design-panel__row--paint">
            {renderAction("changeViewBackgroundColor")}
          </div>
          <h3>Grid</h3>
          <div className="buttonList">
            {(
              [
                { style: null, icon: slashIcon, label: "No grid" },
                { style: "lines", icon: gridIcon, label: "Grid lines" },
                { style: "dots", icon: gridDotsIcon, label: "Grid dots" },
              ] as const
            ).map(({ style, icon, label }) => (
              <button
                key={label}
                type="button"
                className={
                  (
                    style === null
                      ? !appState.gridModeEnabled
                      : appState.gridModeEnabled && appState.gridStyle === style
                  )
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setAppState({
                    gridModeEnabled: style !== null,
                    gridStyle: style ?? appState.gridStyle,
                    // grid snapping and object snapping are mutually
                    // exclusive, same as in actionToggleGridMode
                    objectsSnapModeEnabled: false,
                  })
                }
                title={label}
                aria-label={label}
              >
                {icon}
              </button>
            ))}
          </div>
        </PanelSection>
        {!isBrushTool && (
          <div className="design-panel__empty-state">
            Select an element to edit its properties
          </div>
        )}
      </div>
    );
  }

  const hasSelection = targetElements.length > 0;

  return (
    <div className="design-panel selected-shape-actions">
      {brushSection}
      {hasSelection && (
        <PanelSection title="Position" heading>
          <h3>Alignment</h3>
          <div className="design-panel__align-row buttonList">
            {ALIGN_GROUPS.map((group, groupIndex) => (
              <div className="design-panel__align-group" key={groupIndex}>
                {group.map(({ action, icon, label }) => (
                  <button
                    key={action.name}
                    type="button"
                    // Excalidraw's align actions only align elements
                    // relative to EACH OTHER (no Figma-style align-to-
                    // frame/canvas exists), so with fewer than two
                    // elements selected there is nothing they could do —
                    // disabled rather than hidden so the row's layout
                    // stays stable.
                    disabled={!showAlignActions}
                    onClick={() => actionManager.executeAction(action, "ui")}
                    title={t(label)}
                    aria-label={t(label)}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <h3>Position</h3>
          <div className="design-panel__inputs">
            {singleElement ? (
              <>
                <Position
                  property="x"
                  element={singleElement}
                  elementsMap={elementsMap}
                  scene={scene}
                  appState={appState}
                />
                <Position
                  property="y"
                  element={singleElement}
                  elementsMap={elementsMap}
                  scene={scene}
                  appState={appState}
                />
              </>
            ) : (
              multipleElements && (
                <>
                  <MultiPosition
                    property="x"
                    elements={multipleElements}
                    elementsMap={elementsMap}
                    atomicUnits={atomicUnits}
                    scene={scene}
                    appState={appState}
                  />
                  <MultiPosition
                    property="y"
                    elements={multipleElements}
                    elementsMap={elementsMap}
                    atomicUnits={atomicUnits}
                    scene={scene}
                    appState={appState}
                  />
                </>
              )
            )}
          </div>
          <h3>Rotation</h3>
          <div className="design-panel__row design-panel__row--rotation">
            {singleElement ? (
              <Angle
                property="angle"
                element={singleElement}
                scene={scene}
                appState={appState}
              />
            ) : (
              multipleElements && (
                <MultiAngle
                  property="angle"
                  elements={multipleElements}
                  scene={scene}
                  appState={appState}
                />
              )
            )}
            <div className="buttonList">
              <button
                type="button"
                onClick={stepRotation}
                title="Rotate 45° (0 → 45 → 90 → …)"
              >
                {resetRotationIcon}
              </button>
              <button
                type="button"
                onClick={() =>
                  actionManager.executeAction(actionFlipHorizontal, "ui")
                }
                title={t("labels.flipHorizontal")}
              >
                {flipHorizontal}
              </button>
              <button
                type="button"
                onClick={() =>
                  actionManager.executeAction(actionFlipVertical, "ui")
                }
                title={t("labels.flipVertical")}
              >
                {flipVertical}
              </button>
            </div>
          </div>
        </PanelSection>
      )}

      {hasSelection && (
        <PanelSection title="Layout" heading>
          <h3>Dimensions</h3>
          <div className="design-panel__row design-panel__row--dimensions">
            <div className="design-panel__inputs">
              {singleElement ? (
                <>
                  <Dimension
                    property="width"
                    element={singleElement}
                    scene={scene}
                    appState={appState}
                    shouldKeepAspectRatio={aspectRatioLocked}
                  />
                  <Dimension
                    property="height"
                    element={singleElement}
                    scene={scene}
                    appState={appState}
                    shouldKeepAspectRatio={aspectRatioLocked}
                  />
                </>
              ) : (
                multipleElements && (
                  <>
                    <MultiDimension
                      property="width"
                      elements={multipleElements}
                      elementsMap={elementsMap}
                      atomicUnits={atomicUnits}
                      scene={scene}
                      appState={appState}
                    />
                    <MultiDimension
                      property="height"
                      elements={multipleElements}
                      elementsMap={elementsMap}
                      atomicUnits={atomicUnits}
                      scene={scene}
                      appState={appState}
                    />
                  </>
                )
              )}
            </div>
            <div className="buttonList">
              <button
                type="button"
                className={aspectRatioLocked ? "active" : undefined}
                // proportional stats-resize only exists in the engine for
                // single elements (MultiDimension's handler has no
                // aspect-ratio flag; its multi-element groups are already
                // always proportional) — disabled rather than half-wired
                // for multi-selection.
                disabled={!singleElement}
                onClick={() => setAspectRatioLocked((locked) => !locked)}
                title={
                  aspectRatioLocked
                    ? "Unlock aspect ratio"
                    : "Lock aspect ratio"
                }
                aria-label="Lock aspect ratio"
                aria-pressed={aspectRatioLocked}
              >
                {aspectRatioLocked ? LockedIcon : UnlockedIcon}
              </button>
            </div>
          </div>
        </PanelSection>
      )}

      {/* Abschnitt 18: moved up (was between Stroke and Text) and
          restructured — main heading + labeled Opacity/Corner-radius
          columns. The spec's three extra icons (visibility eye, blend-mode
          drop, per-corner-radius slider) are deliberately NOT rendered:
          this Excalidraw has no per-element visibility flag, no blendMode
          property, and only a single roundness value (verified in
          packages/element/src/types.ts) — per instruction, reported
          instead of wiring fake buttons. */}
      {/* with a brush tool and nothing selected this section's only control
          (opacity) moves to the Brush section — skip the empty heading */}
      {(hasSelection || !isBrushTool || showRoundness) && (
        <PanelSection title="Appearance" heading>
          {hasSelection ? (
            <>
              <div className="design-panel__inputs">
                <div className="design-panel__field">
                  <h3>Opacity</h3>
                  <PropertyNumberInput
                    icon={OpacityGlyph}
                    suffix="%"
                    title="Opacity"
                    value={commonOpacity}
                    min={0}
                    max={100}
                    onCommit={setCustomOpacity}
                  />
                </div>
                {roundnessElements.length > 0 && (
                  <div className="design-panel__field">
                    <h3>Corner radius</h3>
                    <PropertyNumberInput
                      icon={EdgeRoundIcon}
                      title="Corner radius"
                      value={commonCornerRadius}
                      min={0}
                      max={500}
                      onCommit={setCustomCornerRadius}
                      action={
                        perCornerElements.length > 0 && (
                          <button
                            type="button"
                            className={clsx("design-panel__corner-toggle", {
                              active: showIndependentCorners,
                            })}
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={toggleIndependentCorners}
                            title={
                              showIndependentCorners
                                ? "Use one radius for all corners"
                                : "Set each corner independently"
                            }
                            aria-label="Independent corners"
                            aria-pressed={showIndependentCorners}
                          >
                            {IndependentCornersIcon}
                          </button>
                        )
                      }
                    />
                  </div>
                )}
              </div>
              {showIndependentCorners && (
                <div className="design-panel__inputs design-panel__corners">
                  {CORNER_GRID_ORDER.map((index) => (
                    <div
                      className="design-panel__field"
                      key={CORNER_LABELS[index]}
                    >
                      <PropertyNumberInput
                        icon={CORNER_GLYPHS[index]}
                        title={`${CORNER_LABELS[index]} corner radius`}
                        value={commonCornerRadii[index]}
                        min={0}
                        max={500}
                        onCommit={(radius, capture) =>
                          setCornerRadiusAt(index, radius, capture)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
              {showRoundness && renderAction("changeRoundness")}
            </>
          ) : (
            <>
              {/* the Brush section above already owns this same
                  currentItemOpacity — don't show a second control for it */}
              {!isBrushTool && renderAction("changeOpacity")}
              {showRoundness && renderAction("changeRoundness")}
            </>
          )}
        </PanelSection>
      )}

      {/* Abschnitt 19: Fill — main heading row (title only), then swatch+hex
          chip / alpha % / eye (visibility toggle) in one row, then the
          untouched Excalidraw fill-style presets (Hachure/Cross-Hatch/Solid)
          below. The % field edits the alpha channel of backgroundColor — NOT
          element.opacity (Appearance). */}

      {singleFrame && (
        <PanelSection title="Screen">
          <div className="design-panel__inputs">
            <select
              className="design-panel__select"
              title="Screen size preset"
              value={findPresetIndex(singleFrame.width, singleFrame.height)}
              onChange={(event) => {
                const index = Number(event.target.value);
                if (index < 0) {
                  return;
                }
                const preset = SCREEN_SIZE_PRESETS[index];
                applyScreenPreset(preset.width, preset.height);
              }}
            >
              <option value={-1}>Custom</option>
              {SCREEN_SIZE_PRESETS.map((preset, index) => (
                <option key={preset.name} value={index}>
                  {preset.name} ({preset.ratio})
                </option>
              ))}
            </select>
            <PropertyNumberInput
              icon={DurationGlyph}
              suffix="s"
              title="Screen duration (seconds)"
              value={getScreenDurationMs(singleFrame) / 1000}
              min={0.1}
              max={600}
              step={0.1}
              onCommit={setScreenDuration}
            />
          </div>
        </PanelSection>
      )}

      {(showBackground || showFillIcons) && (
        <PanelSection title="Fill" heading>
          {showBackground && (
            <div className="design-panel__row design-panel__row--paint">
              {renderAction("changeBackgroundColor")}
              {hasSelection && fillElements.length > 0 && (
                <>
                  <PropertyNumberInput
                    suffix="%"
                    title="Fill opacity"
                    value={commonFillAlpha}
                    min={0}
                    max={100}
                    onCommit={setFillAlpha}
                  />
                  <button
                    type="button"
                    className="design-panel__row-icon"
                    onClick={() =>
                      togglePaintVisibility(
                        fillElements,
                        fillsHidden,
                        "backgroundColor",
                        hiddenFillColors.current,
                        appState.currentItemBackgroundColor,
                      )
                    }
                    title={fillsHidden ? "Show fill" : "Hide fill"}
                    aria-label={fillsHidden ? "Show fill" : "Hide fill"}
                    aria-pressed={fillsHidden}
                  >
                    {fillsHidden ? eyeClosedIcon : eyeIcon}
                  </button>
                </>
              )}
            </div>
          )}
          {showFillIcons && renderAction("changeFillStyle")}
        </PanelSection>
      )}

      {/* Abschnitt 19: Stroke — same structure as Fill. The spec's third
          row ("Position" dropdown + "Weight" + 2 icons) is deliberately
          not rendered: Excalidraw has no stroke position (strokes are
          always centered on the path — no strokePosition/strokeAlign
          property exists, lineCap/lineJoin are hardcoded "round" in
          renderElement.ts), the weight number field already exists in the
          preserved Strichstärke row directly below (a second synced copy
          would be pure duplication), and no unmapped stroke option remains
          in the codebase for the 2 extra icons (dash pattern = the
          preserved Konturstil row, sloppiness has its own row). See
          Design-Panel-System.md Abschnitt 19/20. */}
      {(showStrokeColor ||
        showStrokeWidth ||
        showStrokeStyle ||
        showFreedrawMode) && (
        <PanelSection title="Stroke" heading>
          {showStrokeColor && (
            <div className="design-panel__row design-panel__row--paint">
              {renderAction("changeStrokeColor")}
              {hasSelection && strokeColorElements.length > 0 && (
                <>
                  <PropertyNumberInput
                    suffix="%"
                    title="Stroke opacity"
                    value={commonStrokeAlpha}
                    min={0}
                    max={100}
                    onCommit={setStrokeAlpha}
                  />
                  <button
                    type="button"
                    className="design-panel__row-icon"
                    onClick={() =>
                      togglePaintVisibility(
                        strokeColorElements,
                        strokesHidden,
                        "strokeColor",
                        hiddenStrokeColors.current,
                        appState.currentItemStrokeColor,
                      )
                    }
                    title={strokesHidden ? "Show stroke" : "Hide stroke"}
                    aria-label={strokesHidden ? "Show stroke" : "Hide stroke"}
                    aria-pressed={strokesHidden}
                  >
                    {strokesHidden ? eyeClosedIcon : eyeIcon}
                  </button>
                </>
              )}
            </div>
          )}
          {showStrokeWidth && (
            <div className="design-panel__row">
              {renderAction("changeStrokeWidth")}
              {hasSelection && strokeWidthElements.length > 0 && (
                <PropertyNumberInput
                  icon={StrokeWidthBaseIcon}
                  title="Custom stroke width"
                  value={commonStrokeWidth}
                  min={0.5}
                  max={50}
                  step={0.5}
                  onCommit={setCustomStrokeWidth}
                />
              )}
            </div>
          )}
          {showFreedrawMode && renderAction("changeFreedrawMode")}
          {showStrokeStyle && (
            <>
              {renderAction("changeStrokeStyle")}
              {renderAction("changeSloppiness")}
            </>
          )}
        </PanelSection>
      )}

      {showTextProps && (
        <PanelSection title="Text">
          <fieldset>{renderAction("changeFontFamily")}</fieldset>
          <div className="design-panel__row">
            {renderAction("changeFontSize")}
            <PropertyNumberInput
              icon={fontSizeIcon}
              title="Custom font size"
              value={commonFontSize}
              min={MIN_FONT_SIZE}
              max={400}
              step={1}
              onCommit={setCustomFontSize}
            />
          </div>
          {renderAction("changeTextRough")}
          {/* roughness is the wobble strength — text has no Stroke section,
              so surface Sloppiness here once handwriting is on */}
          {roughTextOn && renderAction("changeSloppiness")}
          {(appState.activeTool.type === "text" ||
            suppportsHorizontalAlign(targetElements, elementsMap)) &&
            renderAction("changeTextAlign")}
          {shouldAllowVerticalAlign(targetElements, elementsMap) &&
            renderAction("changeVerticalAlign")}
          {textColorElements.length > 0 && (
            <ColorPicker
              topPicks={DEFAULT_ELEMENT_STROKE_PICKS}
              palette={DEFAULT_ELEMENT_STROKE_COLOR_PALETTE}
              type="elementStroke"
              label="Text color"
              color={commonTextColor}
              onChange={setTextColor}
              elements={textColorElements}
              appState={{
                ...appState,
                openPopup: textColorPopupOpen ? "elementStroke" : null,
              }}
              updateData={(data) =>
                setTextColorPopupOpen(data?.openPopup === "elementStroke")
              }
            />
          )}
        </PanelSection>
      )}

      {(showArrowProps || showArrowheads) && (
        <PanelSection title="Arrow">
          {showArrowProps && renderAction("changeArrowType")}
          {showArrowheads && renderAction("changeArrowhead")}
        </PanelSection>
      )}

      {/* Abschnitt 20: Effects — unlike Fill/Stroke (whose heading actions
          live in this file because their eye/opacity/minus logic reads and
          mutates element color props directly here), Effects already
          funnels everything through the single changeEffects action and
          its own PanelComponent — so the heading (title + grid/plus) and
          the active-effects list both live inside EffectsPanel itself,
          rather than duplicating that state-reading logic here too. No
          `title` passed to PanelSection: it only contributes the shared
          section divider/spacing. */}
      {hasSelection && (
        <PanelSection>{renderAction("changeEffects")}</PanelSection>
      )}

      <PanelSection title={t("labels.layers")} heading>
        <div className="buttonList">
          {renderAction("sendToBack")}
          {renderAction("sendBackward")}
          {renderAction("bringForward")}
          {renderAction("bringToFront")}
        </div>
      </PanelSection>

      {!isEditingTextOrNewElement && hasSelection && (
        <PanelSection title={t("labels.actions")} heading>
          <div className="buttonList">
            {renderAction("duplicateSelection")}
            {renderAction("deleteSelectedElements")}
            {renderAction("group")}
            {renderAction("ungroup")}
            {showLinkIcon && renderAction("hyperlink")}
            {showCropEditorAction && renderAction("cropEditor")}
            {showLineEditorAction && renderAction("toggleLinearEditor")}
          </div>
        </PanelSection>
      )}
    </div>
  );
};

export default DesignPanel;
